<template>
  <Teleport to="body">
    <div v-if="state.phase !== 'idle'" class="modal-backdrop voice-call-backdrop">
      <section
        class="voice-call-dialog"
        :class="{ 'video-call-dialog': state.mediaType === 'video' }"
        role="dialog"
        aria-modal="true"
        :aria-label="titleText"
      >
        <header class="voice-call-header">
          <strong>{{ titleText }}</strong>
          <button
            v-if="state.phase === 'ended' || state.phase === 'error'"
            type="button"
            class="icon-button"
            :aria-label="language.t('voiceCall.close')"
            @click="voiceCallService.close"
          >×</button>
        </header>
        <div v-if="state.mediaType === 'video'" class="video-call-layout">
          <div class="video-call-stage">
            <div ref="remoteVideoElement" class="video-call-remote"></div>
            <div v-if="!state.remoteVideoAvailable" class="video-call-remote-placeholder">
              <img v-if="state.peerPortrait" :src="state.peerPortrait" alt="" class="video-call-placeholder-avatar image-avatar">
              <span v-else class="video-call-placeholder-avatar">{{ avatarText }}</span>
              <strong>{{ state.peerName || state.peerUserID }}</strong>
              <span>{{ statusText }}</span>
            </div>
            <div
              ref="localVideoElement"
              v-show="state.cameraEnabled"
              class="video-call-local"
            ></div>
            <div v-if="!state.cameraEnabled" class="video-call-local-placeholder">{{ language.t('voiceCall.cameraDisabled') }}</div>
          </div>
          <div class="video-call-peer-info">
            <strong>{{ state.peerName || state.peerUserID }}</strong>
            <span class="voice-call-status" :aria-live="state.phase === 'incoming' ? 'assertive' : 'polite'">{{ statusText }}</span>
            <time v-if="state.phase === 'active'">{{ elapsedLabel }}</time>
          </div>
        </div>
        <div v-else class="voice-call-peer">
          <img v-if="state.peerPortrait" :src="state.peerPortrait" alt="" class="voice-call-avatar image-avatar">
          <span v-else class="voice-call-avatar">{{ avatarText }}</span>
          <strong>{{ state.peerName || state.peerUserID }}</strong>
          <span class="voice-call-status" :aria-live="state.phase === 'incoming' ? 'assertive' : 'polite'">{{ statusText }}</span>
          <time v-if="state.phase === 'active'">{{ elapsedLabel }}</time>
        </div>
        <p v-if="state.error" class="voice-call-error" role="alert">{{ state.error }}</p>
        <div class="voice-call-controls">
          <template v-if="state.phase === 'incoming'">
            <button type="button" class="voice-call-action decline" @click="voiceCallService.decline">{{ language.t('voiceCall.reject') }}</button>
            <button type="button" class="voice-call-action accept" @click="voiceCallService.accept">{{ language.t(state.mediaType === 'video' ? 'voiceCall.acceptVideo' : 'voiceCall.accept') }}</button>
          </template>
          <button v-else-if="state.phase === 'outgoing'" type="button" class="voice-call-action decline" @click="voiceCallService.end">{{ language.t('voiceCall.cancel') }}</button>
          <template v-else-if="state.phase === 'active'">
            <button type="button" class="voice-call-action secondary" :aria-pressed="state.muted" @click="voiceCallService.setMuted(!state.muted)">
              {{ language.t(state.muted ? 'voiceCall.unmute' : 'voiceCall.mute') }}
            </button>
            <button
              v-if="state.mediaType === 'video'"
              type="button"
              class="voice-call-action secondary"
              :aria-pressed="state.cameraEnabled"
              @click="voiceCallService.setCameraEnabled(!state.cameraEnabled)"
            >{{ language.t(state.cameraEnabled ? 'voiceCall.cameraOff' : 'voiceCall.cameraOn') }}</button>
            <button
              v-if="state.mediaType === 'video' && state.cameraEnabled"
              type="button"
              class="voice-call-action secondary"
              @click="voiceCallService.switchCamera"
            >{{ language.t('voiceCall.switchCamera') }}</button>
            <button type="button" class="voice-call-action decline" @click="voiceCallService.end">{{ language.t('voiceCall.hangUp') }}</button>
          </template>
          <button v-else-if="state.phase === 'connecting'" type="button" class="voice-call-action decline" @click="voiceCallService.end">{{ language.t('voiceCall.hangUp') }}</button>
          <button v-else-if="state.phase === 'ended' || state.phase === 'error'" type="button" class="voice-call-action secondary" @click="voiceCallService.close">{{ language.t('voiceCall.close') }}</button>
          <span v-else class="voice-call-waiting" role="status">{{ statusText }}</span>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useLanguageStore } from '@/stores/language';
import { voiceCallService, voiceCallState } from '@/services/voiceCall';

const language = useLanguageStore();
const state = voiceCallState;
const now = ref(Date.now());
const localVideoElement = ref(null);
const remoteVideoElement = ref(null);
let elapsedTimer = 0;
const avatarText = computed(() => String(state.peerName || state.peerUserID || '?').slice(0, 1).toUpperCase());
const titleText = computed(() => language.t(state.mediaType === 'video' ? 'voiceCall.videoTitle' : 'voiceCall.title'));
const statusText = computed(() => {
  if (state.phase === 'error') return language.t('voiceCall.error');
  if (state.phase === 'ended') return language.t(`voiceCall.${state.result || 'ended'}`);
  if (state.phase === 'incoming' && state.mediaType === 'video') return language.t('voiceCall.incomingVideo');
  return language.t(`voiceCall.${state.phase}`);
});
const elapsedLabel = computed(() => {
  const seconds = Math.max(0, Math.floor((now.value - Number(state.startedAt || now.value)) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
});

onMounted(() => {
  elapsedTimer = window.setInterval(() => { now.value = Date.now(); }, 1000);
});

watch(
  () => [state.phase, state.mediaType, state.videoTrackVersion, state.cameraEnabled],
  async () => {
    await nextTick();
    voiceCallService.attachVideoElements(localVideoElement.value, remoteVideoElement.value);
  },
  { flush: 'post' }
);

onBeforeUnmount(() => {
  window.clearInterval(elapsedTimer);
  voiceCallService.attachVideoElements(null, null);
});
</script>
